/**
 * V112-3-10 (ADR-0099, owner D1–D5): the one scope screen's vocabulary, labels and value plumbing.
 *
 * The values are core's scope lists (`@aigovops/core` scope-model); this module only adds the plain-words labels the
 * screen shows and the conversions between the three shapes an answer takes: a workspace profile column, a system's
 * stored classification (engine fields), and the screen's row value (a string list).
 */
import {
  AFFECTED_PERSON_LABELS,
  DATA_CLASS_LABELS,
  DECISION_TYPES,
  FINAL_SAY,
  FINAL_SAY_OPTIONS,
  INDUSTRY_SECTOR,
  MARKETS,
  SCOPE_AFFECTED_PERSONS,
  SCOPE_DATA_CLASSES,
  US_STATE_TO_CODE,
  finalSayOf,
  type FinalSay,
  type ParsedCustomerProfile,
  type ScopeAnswer,
  type ScopeAnswerMeta,
  type ScopeDefaults,
  type ScopeOverrideMeta,
} from '@aigovops/core';

export interface Option { value: string; label: string }

/** The six inheritable rows. `federal` is two engine answers (role + flow-down) shown as one question. */
export const ROW_KEYS = ['markets', 'data_classes', 'affected_persons', 'decision_types', 'sector', 'federal'] as const;
export type RowKey = (typeof ROW_KEYS)[number];

/** The override-record answers each row stands for. */
export const ROW_ANSWERS: Readonly<Record<RowKey, readonly ScopeAnswer[]>> = {
  markets: ['markets'], data_classes: ['data_classes'], affected_persons: ['affected_persons'],
  decision_types: ['decision_types'], sector: ['sector'], federal: ['deployer_type', 'federal_flowdown'],
};

/** Row names: the workspace names industries, a system holds one sector (D3). */
export const ROW_NAME: Readonly<Record<RowKey, string>> = {
  markets: 'Markets', data_classes: 'Data classes', affected_persons: 'Affected people',
  decision_types: 'Decision types', sector: 'Industries', federal: 'US federal role',
};
export const SYSTEM_ROW_NAME: Readonly<Record<RowKey, string>> = { ...ROW_NAME, sector: 'Sector' };

const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
const humanize = (v: string) => sentence(v.replace(/[_-]+/g, ' '));

const MARKET_LABEL: Readonly<Record<string, string>> = {
  us: 'United States', eu: 'European Union', uk: 'United Kingdom', ca: 'Canada', cn: 'China',
  sg: 'Singapore', jp: 'Japan', in: 'India', au: 'Australia', global: 'Other / worldwide',
};
export const MARKET_OPTIONS: Option[] = MARKETS.map((v) => ({ value: v, label: MARKET_LABEL[v] ?? v }));
/** US state code (core US_STATE_TO_CODE) → "Colorado (US)". */
const US_STATE_NAME: Readonly<Record<string, string>> = Object.fromEntries(Object.entries(US_STATE_TO_CODE).map(([name, code]) => [code.toLowerCase(), name]));
/** A market value in words; never a raw code: "us-co" → "Colorado (US)", else the code upper-cased. */
export const marketLabel = (v: string): string => {
  const k = v.toLowerCase();
  if (MARKET_LABEL[k]) return MARKET_LABEL[k]!;
  if (US_STATE_NAME[k]) return `${US_STATE_NAME[k]} (US)`;
  return k.toUpperCase();
};

/** Data classes the screen offers first (the mockup's set), in plain words; the rest sit behind "More data classes". */
const DATA_PRIMARY: Readonly<Record<string, string>> = {
  pii: 'Personal', phi: 'Health', financial: 'Financial', biometric: 'Biometric',
  cui: 'US government controlled (CUI)', de_identified: 'De-identified', proprietary: 'Confidential business', public: 'Public',
};
const dataLabel = (v: string) => DATA_PRIMARY[v] ?? (v === 'health' ? 'Health (special category)' : null) ?? (v in DATA_CLASS_LABELS ? sentence((DATA_CLASS_LABELS as Record<string, string>)[v]!) : humanize(v));
export const DATA_OPTIONS: Option[] = Object.keys(DATA_PRIMARY).map((v) => ({ value: v, label: dataLabel(v) }));
/** Values that mean the same as an offered one: not offered again (a stored one still reads clearly). */
const DATA_SAME_AS: Readonly<Record<string, string>> = { health: 'phi' };
export const DATA_MORE_OPTIONS: Option[] = SCOPE_DATA_CLASSES.filter((v) => !(v in DATA_PRIMARY) && !(v in DATA_SAME_AS)).map((v) => ({ value: v, label: dataLabel(v) }));

const PEOPLE_PRIMARY = ['consumers', 'workers', 'job_applicants', 'patients', 'students', 'borrowers', 'tenants', 'children'];
const personLabel = (v: string) => (v in AFFECTED_PERSON_LABELS ? sentence((AFFECTED_PERSON_LABELS as Record<string, string>)[v]!) : humanize(v));
export const PEOPLE_OPTIONS: Option[] = PEOPLE_PRIMARY.map((v) => ({ value: v, label: personLabel(v) }));
export const PEOPLE_MORE_OPTIONS: Option[] = SCOPE_AFFECTED_PERSONS.filter((v) => !PEOPLE_PRIMARY.includes(v)).map((v) => ({ value: v, label: personLabel(v) }));

const DECISION_LABEL: Readonly<Record<string, string>> = {
  none: 'None', employment: 'Employment', credit: 'Credit', healthcare: 'Health care', other: 'Another decision about a person',
};
export const DECISION_OPTIONS: Option[] = DECISION_TYPES.map((v) => ({ value: v, label: DECISION_LABEL[v] ?? v }));

const INDUSTRY_LABEL: Readonly<Record<string, string>> = {
  healthcare: 'Healthcare', insurance: 'Insurance', financial_services: 'Financial services', hr_recruiting: 'HR / recruiting',
  retail: 'Retail', saas: 'SaaS / infrastructure', education: 'Education', government: 'Government', legal: 'Legal', media: 'Media',
};
export const INDUSTRY_OPTIONS: Option[] = Object.keys(INDUSTRY_SECTOR).map((v) => ({ value: v, label: INDUSTRY_LABEL[v] ?? v }));
/** An industry in words; a code outside the intake list (e.g. an old NAICS code) reads "Other (5112)". */
export const industryLabel = (v: string): string => INDUSTRY_LABEL[v] ?? `Other (${v})`;
export const isKnownIndustry = (v: string) => v in INDUSTRY_SECTOR;
/** Engine `sector` → the profile industry code it came from. */
export const industryOfSector = (sector: string): string =>
  Object.keys(INDUSTRY_SECTOR).find((k) => INDUSTRY_SECTOR[k] === sector) ?? sector;

export const FEDERAL_OPTIONS: Option[] = [
  { value: 'other', label: 'Neither' }, { value: 'federal_agency', label: 'Federal agency' }, { value: 'government_contractor', label: 'Federal contractor' },
];
export const FLOWDOWN_LABEL = 'Our federal contracts pass down OMB AI clauses';
/** The federal row's value: [] = not answered; [role] or ['government_contractor', 'flowdown']. */
export const FLOWDOWN = 'flowdown';

export const ROW_OPTIONS: Readonly<Record<RowKey, Option[]>> = {
  markets: MARKET_OPTIONS, data_classes: DATA_OPTIONS, affected_persons: PEOPLE_OPTIONS,
  decision_types: DECISION_OPTIONS, sector: INDUSTRY_OPTIONS, federal: FEDERAL_OPTIONS,
};
export const ROW_MORE_OPTIONS: Partial<Record<RowKey, { label: string; options: Option[] }>> = {
  data_classes: { label: 'More data classes', options: DATA_MORE_OPTIONS },
  affected_persons: { label: 'More affected people', options: PEOPLE_MORE_OPTIONS },
};
/** A value that clears the others in a multi-choice row. */
export const ROW_EXCLUSIVE: Partial<Record<RowKey, string>> = { decision_types: 'none' };
export const ROW_HINT: Partial<Record<RowKey, string>> = { decision_types: 'None clears the others' };

export function label(row: RowKey, v: string): string {
  if (row === 'data_classes') return dataLabel(v);
  if (row === 'affected_persons') return personLabel(v);
  if (row === 'markets') return marketLabel(v);
  if (row === 'sector') return industryLabel(v);
  return [...ROW_OPTIONS[row], ...(ROW_MORE_OPTIONS[row]?.options ?? [])].find((o) => o.value === v)?.label ?? humanize(v);
}

/** One row value in words ("United States, European Union"; "Not answered"). */
export function fmt(row: RowKey, val: readonly string[]): string {
  if (!val.length) return 'Not answered';
  if (row === 'federal') {
    const role = val[0]!;
    return label('federal', role) + (role === 'government_contractor'
      ? (val.includes(FLOWDOWN) ? ' · passes down OMB AI clauses' : ' · no OMB AI clauses passed down') : '');
  }
  return val.map((v) => label(row, v)).join(', ');
}

/** The value labels of a row, for a value list (federal is one item). */
export const valueItems = (row: RowKey, val: readonly string[]): string[] =>
  row === 'federal' ? (val.length ? [fmt(row, val)] : []) : val.map((v) => label(row, v));

export const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/** What changed in one answer, in a few words: "+ Canada, − United Kingdom". */
export function changeWords(row: RowKey, before: readonly string[], after: readonly string[]): string {
  if (row === 'federal') return `${fmt(row, before)} → ${fmt(row, after)}`;
  const plus = after.filter((v) => !before.includes(v)).map((v) => `+ ${label(row, v)}`);
  const minus = before.filter((v) => !after.includes(v)).map((v) => `− ${label(row, v)}`);
  return [...plus, ...minus].join(', ') || 'no change';
}

/** Ticking a value in a multi-choice row, honouring the exclusive value ("None clears the others"). */
export function toggleValue(row: RowKey, cur: readonly string[], v: string, on: boolean): string[] {
  const ex = ROW_EXCLUSIVE[row];
  if (!on) return cur.filter((x) => x !== v);
  if (ex && v === ex) return [v];
  return [...cur.filter((x) => x !== ex && x !== v), v];
}

// ---------- Profile ↔ rows ----------

const list = (x: unknown): string[] => (Array.isArray(x) ? x.filter((v): v is string => typeof v === 'string') : []);
const lower = (xs: string[]) => [...new Set(xs.map((x) => x.toLowerCase()))];

export function profileRows(p: Pick<ParsedCustomerProfile, 'geographies' | 'data_types' | 'affected_persons' | 'decision_types' | 'industry_codes' | 'deployer_type' | 'federal_flowdown'>): Record<RowKey, string[]> {
  return {
    markets: lower(list(p.geographies)),
    data_classes: list(p.data_types),
    affected_persons: list(p.affected_persons),
    decision_types: list(p.decision_types),
    sector: list(p.industry_codes),
    federal: p.deployer_type ? [p.deployer_type, ...(p.deployer_type === 'government_contractor' && p.federal_flowdown ? [FLOWDOWN] : [])] : [],
  };
}

export function profileDefaults(p: Parameters<typeof profileRows>[0]): ScopeDefaults {
  return {
    geographies: p.geographies ?? null, data_types: p.data_types ?? null, affected_persons: p.affected_persons ?? null,
    decision_types: p.decision_types ?? null, industry_codes: p.industry_codes ?? null,
    deployer_type: p.deployer_type ?? null, federal_flowdown: p.federal_flowdown ?? null,
  };
}

/** The profile columns for a set of rows (the save patch and the preview's proposed change). */
export function profilePatch(rows: Partial<Record<RowKey, string[]>>) {
  const out: {
    geographies?: string[]; data_types?: string[]; affected_persons?: string[]; decision_types?: string[];
    industry_codes?: string[]; deployer_type?: 'federal_agency' | 'government_contractor' | 'other' | null; federal_flowdown?: boolean | null;
  } = {};
  if (rows.markets) out.geographies = rows.markets;
  if (rows.data_classes) out.data_types = rows.data_classes;
  if (rows.affected_persons) out.affected_persons = rows.affected_persons;
  if (rows.decision_types) out.decision_types = rows.decision_types;
  if (rows.sector) out.industry_codes = rows.sector;
  if (rows.federal) {
    const role = (rows.federal[0] ?? null) as 'federal_agency' | 'government_contractor' | 'other' | null;
    out.deployer_type = role;
    // Flow-down is a contract term, so it only stands for a contractor.
    out.federal_flowdown = role === 'government_contractor' ? rows.federal.includes(FLOWDOWN) : role ? false : null;
  }
  return out;
}

/** The workspace's sector default: only one named industry reaches systems (ADR-0099 §1, D3), and only an industry the
 *  engine knows (core defaultFields maps it through INDUSTRY_SECTOR; an unknown code gives no sector). */
export const inheritedSector = (industries: readonly string[]) => (industries.length === 1 && isKnownIndustry(industries[0]!) ? [...industries] : []);

// ---------- System classification ↔ rows ----------

export type Classification = Record<string, unknown>;

export function parseJson<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback;
  try { const v = JSON.parse(raw) as unknown; return (v ?? fallback) as T; } catch { return fallback; }
}

/** A field at the top level, else its deployment_context mirror (rows from before the top-level fields). */
function field(c: Classification, f: string): unknown {
  if (c[f] !== undefined) return c[f];
  const ctx = c.deployment_context;
  return ctx && typeof ctx === 'object' ? (ctx as Record<string, unknown>)[f] : undefined;
}

export function decisionTypesOf(c: Classification): string[] {
  const out = (['employment', 'credit', 'healthcare'] as const).filter((t) => field(c, `${t}_decision`) === true);
  const consequential = field(c, 'consequential_decision');
  if (out.length) return [...out];
  if (consequential === true) return ['other'];
  if (consequential === false) return ['none'];
  return [];
}

export function classificationRows(c: Classification): Record<RowKey, string[]> {
  const sector = field(c, 'sector');
  const role = field(c, 'deployer_type');
  return {
    markets: lower(list(field(c, 'deployment_jurisdictions'))),
    data_classes: list(field(c, 'data_classes')),
    affected_persons: list(field(c, 'affected_persons')),
    decision_types: decisionTypesOf(c),
    sector: typeof sector === 'string' && sector ? [industryOfSector(sector)] : [],
    federal: typeof role === 'string' && role
      ? [role, ...(role === 'government_contractor' && field(c, 'federal_flowdown') === true ? [FLOWDOWN] : [])] : [],
  };
}

/** The classification PATCH fields a row sets. Federal answers can't be set per system (the PATCH doesn't take them). */
export function rowBody(row: RowKey, val: readonly string[]): Record<string, unknown> | null {
  switch (row) {
    case 'markets': return { deployment_jurisdictions: [...val] };
    case 'data_classes': return { data_classes: [...val] };
    case 'affected_persons': return { affected_persons: [...val] };
    case 'decision_types': {
      const t = val.filter((x) => x !== 'none');
      return {
        consequential_decision: t.length > 0,
        employment_decision: t.includes('employment'), credit_decision: t.includes('credit'), healthcare_decision: t.includes('healthcare'),
      };
    }
    case 'sector': return val[0] ? { sector: INDUSTRY_SECTOR[val[0]] ?? val[0] } : null;
    case 'federal': {
      const role = (val[0] ?? null) as 'federal_agency' | 'government_contractor' | 'other' | null;
      // Flow-down is a contract term, so it only stands for a contractor.
      return { deployer_type: role, federal_flowdown: role === 'government_contractor' ? val.includes(FLOWDOWN) : role ? false : null };
    }
  }
}

// ---------- System-only answers ----------

/** Owner D2: one "Who has the final say?" answer writes the three engine fields (core FINAL_SAY). */
export const FINAL_SAY_LABEL: Readonly<Record<FinalSay, string>> = {
  informs: 'A person decides; the system only informs',
  suggests: 'The system recommends; a person decides each case',
  approves: 'The system decides; a person approves each result first',
  monitors: 'The system decides; a person watches and can step in',
  automatic: 'The system decides; a person can review on request',
};
export const FINAL_SAY_CHOICES: Option[] = FINAL_SAY_OPTIONS.map((k) => ({ value: k, label: FINAL_SAY_LABEL[k] }));
export const systemDecides = (k: string | null) => !!k && k in FINAL_SAY && FINAL_SAY[k as FinalSay].decision_role === 'decides';
export const finalSayFields = (k: FinalSay) => ({ ...FINAL_SAY[k] });
export { finalSayOf };

export const EXPOSURE_CHOICES: Option[] = [
  { value: 'internal', label: 'Internal only' }, { value: 'authenticated', label: 'Signed-in users' }, { value: 'public', label: 'Public internet' },
];
export const OUTPUT_CHOICES: Option[] = [
  { value: 'staff_ui', label: 'Shown to staff' }, { value: 'customer_ui', label: 'Shown to customers' }, { value: 'records', label: 'Written to records' },
  { value: 'other_system', label: 'Sent to another system' }, { value: 'message', label: 'Sent as email or message' },
];
export const TARGET_CHOICES: Option[] = [
  { value: 'saas', label: 'SaaS' }, { value: 'on_premise', label: 'On-premise' }, { value: 'embedded', label: 'Embedded in a product' },
  { value: 'api', label: 'API' }, { value: 'agent', label: 'Agent' },
];
export const LEVEL_CHOICES: Option[] = [{ value: 'low', label: 'Low' }, { value: 'moderate', label: 'Moderate' }, { value: 'high', label: 'High' }];
/** Older output values that are an offered option under another key: read and saved as that option. */
export const OUTPUT_ALIAS: Readonly<Record<string, string>> = { end_user: 'customer_ui' };
export const canonicalOutputs = (xs: readonly string[]) => [...new Set(xs.map((x) => OUTPUT_ALIAS[x] ?? x))];
export const CIA_DIMS = [['confidentiality', 'Confidentiality'], ['integrity', 'Integrity'], ['availability', 'Availability']] as const;
export const GPAI_CHOICES: Option[] = [
  { value: 'provider', label: 'We train or sell a general-purpose model' },
  { value: 'deployer', label: 'We call one inside this system (e.g. Claude, GPT)' },
  { value: 'none', label: 'Neither' },
];
/** Older stored values that map onto a current option's words. */
const LEGACY_CHOICE_LABEL: Readonly<Record<string, string>> = {
  end_user: 'Shown to customers', customers: 'Customers', employees: 'Employees', internal_only: 'Internal only', external: 'Public internet',
};
/** An option's words; a stored value outside the list is never shown as a raw code. */
export const choiceLabel = (choices: Option[], v: string) => choices.find((o) => o.value === v)?.label ?? LEGACY_CHOICE_LABEL[v] ?? humanize(v);

// ---------- Who and when (D4) ----------

/** "Priya Ramanathan" → "Priya R."; no name → "a team member". */
export function shortName(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'a team member';
  return parts.length === 1 ? parts[0]! : `${parts[0]} ${parts[parts.length - 1]!.charAt(0)}.`;
}
export const shortDay = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
/** The who/when half of a stamp; no record = carried over from before the change ("before Oct 2026"). */
export const stampWho = (m: ScopeAnswerMeta | null | undefined) => (m ? `${shortName(m.by_name)}, ${shortDay(m.at)}` : 'before Oct 2026');

/** A row's stamp: role and flow-down share one (the role's when both exist). */
export function rowMeta(meta: ScopeOverrideMeta | null, row: RowKey): ScopeAnswerMeta | null {
  if (!meta) return null;
  for (const a of ROW_ANSWERS[row]) if (meta[a]) return meta[a]!;
  return null;
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** A scope-driven removal is not a legal "Not applicable" (Priya R2). */
export const REMOVE_NOTE = 'Evidence and tasks stay; the obligation moves to Out of scope (answers changed)';

// ---------- What a rule depends on (V112-3-10: "Leads to" and the preview's causes) ----------

/** A preview rule's `depends_on` key → the screen row it is asked on (federal role + flow-down share one row). */
export const depRow = (key: string): string => (key === 'deployer_type' || key === 'federal_flowdown' ? 'federal' : key);

const DEP_NAME: Readonly<Record<string, string>> = {
  markets: 'Markets', data_classes: 'Data classes', affected_persons: 'Affected people', decision_types: 'Decision types', sector: 'Sector',
  federal: 'US federal role', final_say: 'Final say', gpai_role: 'GPAI role', security_levels: 'Security levels', exposure: 'Exposure',
  deployment_target: 'Deployment target', output_destinations: 'Output destinations', embedded_third_party_ai: 'Embedded AI',
  affected_person_categories: 'Affected people',
};
export const depName = (key: string) => DEP_NAME[depRow(key)] ?? key;

/** A rule's dependencies as screen rows, in order, without repeats. */
export const depRows = (deps: readonly string[] | undefined): string[] => [...new Set((deps ?? []).map(depRow))];

/**
 * "Markets: Canada" — an answer's current value, short. Markets name only the market the rule's jurisdiction is in
 * (when it is one of the answer's markets); system-only answers come from `onlyText`.
 */
export function depText(row: string, rows: Record<RowKey, string[]>, onlyText: (row: string) => string | null, jurisdiction?: string): string | null {
  let value: string;
  if ((ROW_KEYS as readonly string[]).includes(row)) {
    const r = row as RowKey;
    let v = rows[r];
    if (r === 'markets' && jurisdiction) {
      // The market the rule's jurisdiction is in ("US-CO" → Colorado, else United States); none matches → no clause.
      const j = jurisdiction.toLowerCase();
      const hit = [j, j.split('-')[0]!].find((c) => v.includes(c));
      if (!hit) return null;
      v = [hit];
    }
    value = fmt(r, v);
  } else value = onlyText(row) ?? 'Not answered';
  return `${depName(row)}: ${value}`;
}

/** A system-only answer's value in words, read from a stored classification (null = not answered). */
export function classificationOnlyText(c: Classification, key: string): string | null {
  const s = (x: unknown) => (typeof x === 'string' && x ? x : null);
  switch (key) {
    case 'final_say': { const f = finalSayOf(c); return f ? FINAL_SAY_LABEL[f] : null; }
    case 'gpai_role': { const v = s(c.gpai_role); return v ? choiceLabel(GPAI_CHOICES, v) : null; }
    case 'exposure': { const v = s(c.exposure_level); return v ? choiceLabel(EXPOSURE_CHOICES, v) : null; }
    case 'deployment_target': { const v = s(c.deployment_target); return v ? choiceLabel(TARGET_CHOICES, v) : null; }
    case 'output_destinations': { const v = list(c.output_destinations); return v.length ? v.map((x) => choiceLabel(OUTPUT_CHOICES, x)).join(', ') : null; }
    case 'embedded_third_party_ai': return Array.isArray(c.embedded_third_party_ai) ? (list(c.embedded_third_party_ai).join(', ') || 'None') : null;
    case 'security_levels': {
      const cia = (c.data_classification && typeof c.data_classification === 'object' ? c.data_classification : {}) as Record<string, unknown>;
      return CIA_DIMS.some(([d]) => s(cia[d])) ? CIA_DIMS.map(([d, l]) => `${l} ${s(cia[d]) ? choiceLabel(LEVEL_CHOICES, cia[d] as string) : 'not answered'}`).join(', ') : null;
    }
    default: return null;
  }
}
